<template>
  <div class="custom-quill">
    <div ref="quillContainer" />
    <!-- HTML Source Dialog -->
    <v-dialog v-model="showHtmlDialog" persistent max-width="800px">
      <v-card :color="isDarkTheme ? '#2d2d2d' : 'rgba(255,255,255,.95)'">
        <v-card-title class="text-h6 pt-3 pb-0">Edit HTML Source</v-card-title>
        <v-card-text>
          <v-textarea
            v-model="htmlContent"
            rows="12"
            auto-grow
            class="html-textarea"
            variant="outlined"
          ></v-textarea>
        </v-card-text>
        <v-card-actions class="justify-end px-4 pb-4">
          <v-btn color="primary" @click="applyHtml">Apply</v-btn>
          <v-btn @click="closeDialog" class="ml-2">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showImageUrlDialog" persistent max-width="500px">
      <v-card :color="isDarkTheme ? '#2d2d2d' : 'rgba(255,255,255,.95)'">
        <v-card-title class="text-h6 pt-3 pb-0"
          >Insert Image by URL</v-card-title
        >
        <v-card-text>
          <v-text-field
            v-model="imageUrlInput"
            label="Paste image URL here"
            variant="underlined"
            class="img-url-field"
          ></v-text-field>
        </v-card-text>
        <v-card-actions class="justify-end px-4 pb-4">
          <v-btn color="primary" @click="insertImageUrl">Insert</v-btn>
          <v-btn @click="closeImageUrlDialog" class="ml-2">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
  import { ref, watch, onMounted, markRaw, computed, defineExpose } from 'vue'
  import Quill from 'quill/core'
  const Delta = Quill.import('delta')

  import Toolbar from 'quill/modules/toolbar'
  import Snow from 'quill/themes/snow'
  import Clipboard from 'quill/modules/clipboard'
  import History from 'quill/modules/history'
  import BlotFormatter2 from '@enzedonline/quill-blot-formatter2'
  import { uploadImageToFirebase } from '@/helpers/uploadImage'
  import '@enzedonline/quill-blot-formatter2/dist/css/quill-blot-formatter2.css'
  import { currentTheme } from '@/plugins/vuetify'
  import { getFullEditorModules } from '@/QuillConfig'

  import 'quill/dist/quill.snow.css'

  Quill.register('modules/toolbar', Toolbar)
  Quill.register('modules/clipboard', Clipboard)
  Quill.register('modules/history', History)
  Quill.register('modules/blotFormatter2', BlotFormatter2)
  Quill.register('theme/snow', Snow)

  const props = defineProps({
    modelValue: { type: String, default: '' },
    content: { type: String, default: undefined },
    placeholder: { type: String, default: 'Enter text here...' },
    allowImages: { type: Boolean, default: true },
  })

  const emit = defineEmits([
    'update:modelValue', // default v-model
    'update:content', // named v-model:content
    'ready',
    'text-change',
  ])

  const isDarkTheme = computed(() => currentTheme.value === 'dark')

  const quill = ref(null)
  const quillContainer = ref(null)
  const showHtmlDialog = ref(false)
  const htmlContent = ref('')

  const showImageUrlDialog = ref(false)
  const imageUrlInput = ref('')

  function openInsertImageUrlDialog() {
    imageUrlInput.value = ''
    showImageUrlDialog.value = true
  }

  function closeImageUrlDialog() {
    showImageUrlDialog.value = false
  }

  function insertImageUrl() {
    // If images disallowed, do nothing (belt-and-suspenders)
    if (!props.allowImages) return

    showImageUrlDialog.value = false
    if (!imageUrlInput.value) return

    let index
    try {
      const range = quill.value.getSelection(true)
      index =
        range && typeof range.index === 'number'
          ? range.index
          : quill.value.getLength()
    } catch (e) {
      index = quill.value.getLength()
    }

    try {
      const length = quill.value.getLength()
      if (index === length - 1) {
        quill.value.insertText(index, '\n', 'user')
        index += 1
      }

      quill.value.insertEmbed(index, 'image', imageUrlInput.value, 'user')
      quill.value.insertText(index + 1, '\n', 'user')
      quill.value.setSelection(index + 2, 0, 'user')
    } catch (err) {
      alert('Could not insert image. Please try again.')
      console.error('insertEmbed error', err)
    }
  }

  const handleImageUpload = () => {
    if (!props.allowImages) return
    // console.log('[ImageUpload] Button clicked')
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.click()

    input.onchange = async () => {
      const file = input.files?.[0]
      // console.log('[ImageUpload] File selected:', file)
      if (!file) return

      try {
        const url = await uploadImageToFirebase(file)
        // console.log('[ImageUpload] Uploaded to Firebase:', url)

        quill.value && quill.value.focus()

        setTimeout(() => {
          let range = null
          try {
            range = quill.value.getSelection(true)
          } catch (err) {
            console.warn('[ImageUpload] getSelection(true) error:', err)
          }

          let index =
            range && typeof range.index === 'number'
              ? range.index
              : quill.value.getLength()

          const length = quill.value.getLength()

          try {
            // Ensure there's a line to insert into if at end
            if (index === length - 1) {
              quill.value.insertText(index, '\n', 'user')
              index += 1
            }

            quill.value.insertEmbed(index, 'image', url, 'user')
            quill.value.insertText(index + 1, '\n', 'user') // Ensure cursor has space
            quill.value.setSelection(index + 2, 0, 'user')
          } catch (err) {
            console.error('[ImageUpload] insertEmbed failed:', err)
          }
        }, 0)
      } catch (err) {
        console.error('🔥 Firebase upload failed', err)
      }
    }
  }

  function openHtmlDialog() {
    htmlContent.value = quill.value.root.innerHTML
    showHtmlDialog.value = true
  }
  function applyHtml() {
    showHtmlDialog.value = false

    let html = htmlContent.value || ''
    // ✅ Strip <img> if images are disabled (prevents bypass via HTML dialog)
    if (!props.allowImages) {
      html = html.replace(/<img\b[^>]*>/gi, '')
    }

    // push the edited HTML into the editor
    if (quill.value?.root) quill.value.root.innerHTML = html

    // then notify parent (supports both v-model styles)
    emit('update:modelValue', html)
    emit('update:content', html)
  }

  function closeDialog() {
    showHtmlDialog.value = false
  }

  const getHtml = () => {
    const html = quill.value?.root?.innerHTML || ''
    return html === '<p><br></p>' ? '' : html
  }

  onMounted(() => {
    quill.value = markRaw(
      new Quill(quillContainer.value, {
        theme: 'snow',
        placeholder: props.placeholder, // ✅ Pass it to Quill
        modules: getFullEditorModules(
          quill,
          openHtmlDialog,
          openInsertImageUrlDialog,
          handleImageUpload,
          { allowImages: props.allowImages } // ✅ pass the toggle
        ),
      })
    )

    quill.value.clipboard.addMatcher('SPAN', (node, delta) => {
      const className = node.getAttribute('class')
      if (className) {
        delta.ops.forEach((op) => {
          if (!op.attributes) op.attributes = {}
          op.attributes.class = className
        })
      }
      return delta
    })

    quill.value.root.innerHTML = props.content ?? props.modelValue ?? ''

    quill.value.on('text-change', () => {
      const html = getHtml()
      emit('update:modelValue', html) // keep current usage working
      emit('update:content', html) // enable v-model:content
      emit('text-change')
    })

    emit('ready')

    // ✅ Hard-block image paste/drag when disabled
    if (!props.allowImages) {
      // Block clipboard image files (e.g., screenshots)
      quill.value.root.addEventListener('paste', (e) => {
        const items = Array.from(e.clipboardData?.items || [])
        if (items.some((it) => it.type?.startsWith('image/'))) {
          e.preventDefault()
          e.stopPropagation()
        }
      })
      // Block drag & drop image files
      quill.value.root.addEventListener('drop', (e) => {
        const files = Array.from(e.dataTransfer?.files || [])
        if (files.some((f) => f.type?.startsWith('image/'))) {
          e.preventDefault()
          e.stopPropagation()
        }
      })
      // Strip pasted HTML <img> (copied from webpages, etc.)
      quill.value.clipboard.addMatcher('IMG', () => new Delta())
    }
  })

  // Existing: supports default v-model
  watch(
    () => props.modelValue,
    (newVal) => {
      if (
        quill.value &&
        quill.value.root.innerHTML !== newVal &&
        document.activeElement !== quill.value.root
      ) {
        quill.value.root.innerHTML = newVal
      }
    }
  )

  // New: supports v-model:content
  watch(
    () => props.content,
    (newVal) => {
      if (
        newVal !== undefined &&
        quill.value &&
        quill.value.root.innerHTML !== newVal &&
        document.activeElement !== quill.value.root
      ) {
        quill.value.root.innerHTML = newVal
      }
    }
  )

  // expose editor for Options API parent ($refs.getEditor())
  const getEditor = () => quill.value
  defineExpose({ getEditor })
</script>

<style>
  .custom-quill {
    background-color: #ffffff;
    display: flex;
    flex-direction: column;
  }
  .v-theme--dark .custom-quill {
    background-color: #2e2e2e;
    color: #f0f0f0;
  }

  .html-editor {
    width: 100%;
    font-family: monospace;
    font-size: 14px;
    padding: 1rem;
    background: #f4f4f4;
    border-top: 0px;
    border: 1px solid #ccc;
  }

  .v-theme--dark .html-editor {
    background: #4c4c4c;
    color: #fff;
  }

  .custom-quill button.ql-insertImageUrl {
    display: flex;
    align-items: center;
  }
  .ql-toolbar .ql-insertImageUrl::before {
    content: '🌐'; /* or any emoji/icon */
    font-size: 16px;
    padding: 0 6px;
  }
  .custom-quill .ql-toolbar .ql-html {
    display: flex;
    align-items: center;
    font-size: 14px;
    font-weight: 500;
    padding: 0 8px;
    cursor: pointer;
  }
  .custom-quill .ql-toolbar button.ql-html::before {
    content: 'HTML'; /* or 'HTML' or an icon */
  }
  .custom-quill .ql-toolbar .ql-html-view,
  .custom-quill .ql-toolbar .ql-html-view:focus {
    color: #444444;
  }
  .custom-quill .ql-toolbar .ql-html-view:hover,
  .custom-quill .ql-toolbar .ql-html-view.active {
    color: #1976d2;
  }
  .v-theme--dark .custom-quill .ql-toolbar .ql-html-view,
  .v-theme--dark .custom-quill .ql-toolbar .ql-html-view:focus {
    color: #8d8d8d;
  }
  .v-theme--dark .custom-quill .ql-toolbar .ql-html-view:hover,
  .v-theme--dark .custom-quill .ql-toolbar .ql-html-view.active {
    color: #90caf9;
  }
  .html-dialog-overlay {
    position: fixed;
    z-index: 9000;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  /* Scoped override for your specific textarea wrapper */
  .html-textarea.v-input--focused {
    max-width: none !important;
    min-width: 100% !important;
  }
  /* Stop shrinking on focus */
  .img-url-field.v-input--focused {
    max-width: none !important;
    min-width: 100% !important;
  }
  .html-dialog {
    background: #fff;
    padding: 2em;
    border-radius: 10px;
    min-width: 400px;
    width: 60%;
    box-shadow: 0 2px 10px #0003;
  }
  .img-url-modal {
    width: 40%;
  }
</style>
